import { DefaultButton, DetailsList, DetailsListLayoutMode, Dropdown, IColumn, IDetailsListProps, IDropdownOption, ISelectionOptionsWithRequiredGetKey, SearchBox, Selection, SelectionMode } from '@fluentui/react';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useAnimatedPaging, { slideAnimationCss } from '../../shared/hooks/useAnimatedPaging';
import useDetailsListNoItemsTextRenderer from '../../shared/hooks/useDetailsListNoItemsTextRenderer';
import { ListDiv } from '../../shared/styled-components';

const PagedDetailsListContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 0.5rem;
  overflow: hidden;

  div.ms-Viewport {
    overflow: auto;
    flex-grow: 1;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 0.5rem;
`;

const FooterContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 1fr;
  align-items: center;
  justify-items: center;

  > :first-child {
    justify-self: start;
  }

  > :last-child {
    justify-self: end;
  }
`;

const PageSizeContainer = styled.div`
  min-width: 235px;
  display: flex;
`;

const SearchAndFilterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const ListContainer = styled(ListDiv)`
  ${slideAnimationCss}
`;

type PageSizeProps = {
    allowPageSizeChange?: false;
    pageSize: number;
    onPageSizeChange?: undefined;
} | {
    allowPageSizeChange: true;
    pageSize?: undefined;
    onPageSizeChange(size: number): void;
};

type SearchProps = {
    allowSearch?: false;
    searchPlaceholder?: undefined;
    onSearch?: undefined;
    onSearchTextChange?: undefined;
    onSearchTextClear?: undefined;
} | {
    allowSearch: true;
    searchPlaceholder?: string;
    onSearch(text: string): void;
    onSearchTextChange(text: string | undefined): void;
    onSearchTextClear(): void;
};

type FilterProps = {
    allowFilter?: false;
    filterOptions?: undefined;
    selectedFilterKey?: undefined;
    onFilterChange?: undefined;
} | {
    allowFilter: true;
    filterOptions: IDropdownOption[];
    selectedFilterKey: string;
    onFilterChange(option: IDropdownOption | undefined): void;
};

type SelectionProps<T> = {
    selectEnabled?: false;
    onSelectionChange?(value: T[]): void;
    getKey?(item: T, index?: number): string;
    selectionRef?: MutableRefObject<{ clearSelection(): void }>;
} | {
    selectEnabled: true;
    onSelectionChange(value: T[]): void;
    getKey?(item: T, index?: number): string;
    selectionRef?: MutableRefObject<{ clearSelection(): void }>;
};

type IProps<T> = PageSizeProps & SearchProps & FilterProps & SelectionProps<T> & {
    // details list controls
    total: number;
    isLoading?: boolean;
    items: T[];
    itemsName?: string;
    columns: IColumn[];
    onActiveItemChanged?(item: T): void;
    styles?: IDetailsListProps['styles'];
    // default paging props
    pageIndex: number;
    onPrevPageClick(): void;
    onNextPageClick(): void;
    layoutMode?: DetailsListLayoutMode;
};

const defaultGetKey = (_: unknown, index: number | undefined) => index?.toString() ?? '';

const pageSizes = [10, 15, 20, 50, 100].map(x => ({ key: x, text: x.toString() }));

export function getDefaultPageSize(): number {
    const pageSize = localStorage.getItem('defaultPageSize');
    return pageSize ? Number.parseInt(pageSize) : 20;
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
const PagedDetailsList = <T,>(props: IProps<T>): JSX.Element => {
    const {
        total,
        isLoading,
        items,
        itemsName,
        columns,
        onActiveItemChanged,
        styles,
        pageIndex,
        onPrevPageClick,
        onNextPageClick,
        allowPageSizeChange = false,
        pageSize,
        onPageSizeChange,
        allowSearch = false,
        searchPlaceholder = `Search ${itemsName ?? ''}`,
        onSearch,
        onSearchTextChange,
        onSearchTextClear,
        allowFilter = false,
        filterOptions = [],
        selectedFilterKey,
        onFilterChange,
        selectEnabled,
        onSelectionChange,
        getKey,
        selectionRef,
        layoutMode,
    } = props;

    const [size, setSize] = useState((!allowPageSizeChange && pageSize) ? pageSize : getDefaultPageSize());
    const updatePageSize = useCallback(
        (num: number) => {
            localStorage.setItem('defaultPageSize', num.toString());
            setSize(num);
            onPageSizeChange?.(num);
        },
        [onPageSizeChange],
    );

    const totalPages = Math.ceil(total / size) || 1;

    const onRenderDetailsFooter = useDetailsListNoItemsTextRenderer(items.length, isLoading, itemsName);

    const { tabIndex, animationClass, navigateToPage } = useAnimatedPaging();

    useEffect(
        () => navigateToPage(pageIndex),
        [navigateToPage, pageIndex],
    );

    const selection = useMemo(
        () => {
            const selectionOptions: ISelectionOptionsWithRequiredGetKey<T> = {
                getKey: getKey ?? defaultGetKey,
                items: [],
                onSelectionChanged: () => onSelectionChange?.(selection.getSelection()),
                onItemsChanged: () => { onSelectionChange?.([]); selection.setAllSelected(false); },
            };

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return new Selection<T>(...[selectionOptions] as any); // because fuck typescript and Fluent UI in particular
        },
        [getKey, onSelectionChange],
    );

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const innerSelectionRef = useRef<Selection<T>>(undefined!);
    innerSelectionRef.current = selection;

    if (selectionRef !== undefined) {
        selectionRef.current = { clearSelection: () => innerSelectionRef.current.setAllSelected(false) };
    }

    return <PagedDetailsListContainer>
        <HeaderContainer>
            <PageSizeContainer style={{ alignItems: (allowPageSizeChange ? 'baseline' : 'center') }}>
                {
                    allowPageSizeChange
                        ?
                        <>
                            <span>Display</span>
                            <Dropdown
                                options={pageSizes}
                                selectedKey={size}
                                onChange={onPageSizeChange && ((_, option) => updatePageSize(option?.key as number))}
                                style={{ marginLeft: 5, marginRight: 5 }}
                            />
                            <span>of total <strong>{total}</strong> {itemsName ?? ''}</span>
                        </>
                        :
                        <span><strong>{total}</strong> {itemsName ?? ''} found</span>
                }
            </PageSizeContainer>
            <SearchAndFilterContainer>
                {
                    allowSearch &&
                    <SearchBox
                        styles={{ root: { width: 225 } }}
                        placeholder={searchPlaceholder}
                        onSearch={onSearch}
                        onClear={onSearchTextClear}
                        onChange={onSearchTextChange && ((_, text) => onSearchTextChange(text))}
                    />
                }
                {
                    allowFilter &&
                    <Dropdown
                        style={{ marginLeft: '0.5rem' }}
                        styles={{ root: { width: 130 } }}
                        options={filterOptions}
                        selectedKey={selectedFilterKey}
                        onChange={onFilterChange && ((_, option) => onFilterChange(option))}
                    />
                }
            </SearchAndFilterContainer>
        </HeaderContainer>
        <ListContainer flexGrow key={tabIndex} className={animationClass}>
            <DetailsList
                // onShouldVirtualize={() => false}
                layoutMode={layoutMode ?? DetailsListLayoutMode.fixedColumns}
                selectionMode={selectEnabled ? SelectionMode.multiple : SelectionMode.none}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                selection={selection as any}
                getKey={getKey ?? defaultGetKey}
                columns={columns}
                items={items}
                onActiveItemChanged={selectEnabled ? undefined : onActiveItemChanged}
                onItemInvoked={selectEnabled ? onActiveItemChanged : undefined}
                onRenderDetailsFooter={onRenderDetailsFooter}
                styles={styles}
            />
        </ListContainer>
        <FooterContainer>
            <DefaultButton onClick={onPrevPageClick} disabled={pageIndex <= 0}>Previous</DefaultButton>
            <span>Page {pageIndex + 1} of {totalPages}</span>
            <DefaultButton onClick={onNextPageClick} disabled={pageIndex >= totalPages - 1}>Next</DefaultButton>
        </FooterContainer>
    </PagedDetailsListContainer>;
};

export default PagedDetailsList;